import { Tooltip } from "antd-v5";
import classNames from "classnames";
import Vocabulary from "components/Vocabulary";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import { isNil } from "lodash";
import React from "react";
import { Link } from "react-router-dom";

import "./ACRiskScoreTag.less";

export default function ACRiskScoreTag({
  value,
  size = "md",
  dot,
  customValue,
  className,
  ...restDivProps
}: Props & Omit<React.HTMLAttributes<HTMLDivElement>, "className">) {
  const featureFlags = useFeatureFlag();

  const { riskScoreEnabled, riskScoreInverted, riskScoreRange } =
    useGlobalSettingsContext();
  const prefixCls = "ac-riskscore";

  let riskClassName,
    isNull = false;
  if (isNil(value) || (!riskScoreEnabled && isNil(customValue))) {
    isNull = true;
    riskClassName = `${prefixCls}-null`;
  } else if (featureFlags.RISK_SCORE_3_0 && riskScoreRange) {
    if (value > riskScoreRange[2]) {
      riskClassName = `${prefixCls}-verypoor`;
    } else if (value > riskScoreRange[1] && value <= riskScoreRange[2]) {
      riskClassName = `${prefixCls}-poor`;
    } else if (value > riskScoreRange[0] && value <= riskScoreRange[1]) {
      riskClassName = `${prefixCls}-fair`;
    } else if (value <= riskScoreRange[0]) {
      riskClassName = `${prefixCls}-good`;
    }
  } else {
    if (riskScoreInverted) {
      value = 100 - value;
    }
    if (value <= 5) {
      riskClassName = `${prefixCls}-verypoor`;
    } else if (value <= 20 && value > 5) {
      riskClassName = `${prefixCls}-poor`;
    } else if (value <= 80 && value > 20) {
      riskClassName = `${prefixCls}-fair`;
    } else if (value <= 95 && value > 80) {
      riskClassName = `${prefixCls}-good`;
    } else {
      riskClassName = `${prefixCls}-good`;
    }
  }
  const content = dot ? (
    <div
      {...restDivProps}
      className={classNames(`${prefixCls}-tag-dot`, riskClassName)}
    />
  ) : (
    <div
      {...restDivProps}
      className={classNames(
        `${prefixCls}-tag`,
        `${prefixCls}-tag-${size}`,
        riskClassName,
        className,
      )}
    >
      {isNull ? "N/A" : !isNil(customValue) ? customValue : value}
    </div>
  );

  if (isNil(customValue) && !riskScoreEnabled) {
    return (
      <Tooltip
        title={
          <>
            <Vocabulary name="riskScore" /> is disabled. Please visit the global
            settings by clicking the risk score card
          </>
        }
      >
        <Link
          to={
            featureFlags.RISK_SCORE_3_0
              ? "/globalsettings?activeStep=assetScore"
              : "/globalsettings?activeStep=step4"
          }
        >
          {content}
        </Link>
      </Tooltip>
    );
  }

  return content;
}

interface Props {
  value: number | null | undefined;
  size?: "xl" | "lg" | "md";
  dot?: boolean;
  customValue?: React.ReactNode;
  className?: string;
}
